import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Select, DatePicker, Spin, Drawer, Switch } from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { functionService } from "../../lib/functions";
import Sidemenu from "../../components/sidemenu";
import * as Styled from "./style";

const ALLOWED_ACCESS = ["0", "1", "2"];
const hasAccess = () => ALLOWED_ACCESS.includes(String(localStorage.getItem("adminAccess")));

/* Earliest date with data, same as Performance Report */
const MIN_DATE = dayjs("2026-04-01");

/* Longest custom range (the backend allows a little over a year) */
const MAX_RANGE_DAYS = 366;

const RECRUITER_PALETTE = [
  { bg: "#EDE9FE", fg: "#5B21B6" },
  { bg: "#FCE7F3", fg: "#9D174D" },
  { bg: "#D1FAE5", fg: "#065F46" },
  { bg: "#FEF3C7", fg: "#92400E" },
  { bg: "#DBEAFE", fg: "#1E40AF" },
  { bg: "#FFE4E6", fg: "#9F1239" },
];

/* ---------- helpers ---------- */

const n = (v) => Number(v) || 0;
const ratio = (a, b) => (n(b) ? n(a) / n(b) : null);
const perDay = (a, days) => {
  const r = ratio(a, days);
  return r == null ? "–" : r.toFixed(1);
};
const pct = (a, b) => {
  const r = ratio(a, b);
  return r == null ? "–" : `${Math.round(r * 100)}%`;
};
/* Attendance colour: below 35% red, 35–70% yellow, above 70% green.
   Used for the table pill (Numbers and Percentages) and the drawer. */
const attendanceTone = (p) => {
  const v = Math.round(n(p));
  if (v > 70) return "good";
  if (v >= 35) return "ok";
  return "low";
};
const dayLabel = (iso) => dayjs(iso).format("ddd, DD MMM");
const hasActivity = (c) => n(c.present_days) > 0 || n(c.sourced) > 0 || n(c.sent) > 0;

/* Pipeline columns. plain = bold number like "Profiles Submitted" in Performance Report */
const PIPELINE = [
  { key: "sourced", label: "Sourced", head: ["#E0E7FF", "#3730A3"], plain: true, rate: (c) => `${perDay(c.sourced, c.present_days)}/day` },
  { key: "assessed", label: "Assessed", head: ["#EDE9FE", "#5B21B6"], pill: ["#EDE9FE", "#5B21B6"], rate: (c) => pct(c.assessed, c.sourced) },
  { key: "sent", label: "Sent", head: ["#D1FAE5", "#065F46"], plain: true, rate: (c) => `${perDay(c.sent, c.present_days)}/day` },
];

/* Sent breakdown columns. These always add up to "Sent". */
const OUTCOMES = [
  { key: "hold", label: "Hold & Req changes", color: "#F59E0B", pill: ["#FEF3C7", "#92400E"] },
  { key: "dup", label: "Duplicate", color: "#9CA3AF", pill: ["#F3F4F6", "#4B5563"] },
  { key: "screen_reject", label: "Screen Reject", color: "#EF4444", pill: ["#FEE2E2", "#991B1B"] },
  { key: "backout", label: "Backout", color: "#8B5CF6", pill: ["#FCE7F3", "#9D174D"] },
  { key: "pf", label: "Yet to be shortlisted", color: "#10B981", pill: ["#DBEAFE", "#1E40AF"] },
  { key: "others", label: "Others", color: "#3B82F6", pill: ["#E0F2FE", "#0C4A6E"] },
];

const SUM_KEYS = [
  "working_days",
  "present_days",
  "absent_days",
  "saturdays_worked",
  "sourced",
  "assessed",
  "sent",
  ...OUTCOMES.map((o) => o.key),
];

/* Adds a recruiter's months together into one record of the same shape */
const aggregateMonths = (monthsMap = {}) => {
  const parts = Object.values(monthsMap).filter(Boolean);
  if (parts.length === 0) return null;
  const t = {};
  SUM_KEYS.forEach((k) => {
    t[k] = parts.reduce((acc, p) => acc + n(p[k]), 0);
  });
  t.absent_dates = parts.flatMap((p) => p.absent_dates || []);
  t.attendance_percentage = t.working_days ? (t.present_days * 100) / t.working_days : 0;
  return t;
};

/* ---------- date ranges (same tabs as Performance Report) ---------- */

const toYMD = (d) => dayjs(d).format("YYYY-MM-DD");
const todayYMD = () => toYMD(dayjs());

/* Steps back `stepsBack` working days (skipping Sat/Sun).
   1 = last working day (Monday -> Friday), 2 = the one before that. */
const previousWorkingDay = (fromDate, stepsBack) => {
  let d = dayjs(fromDate);
  let remaining = stepsBack;
  while (remaining > 0) {
    d = d.subtract(1, "day");
    if (d.day() !== 0 && d.day() !== 6) remaining -= 1;
  }
  return toYMD(d);
};

/* Monday of the current week (on a Sunday this is 6 days back, not tomorrow) */
const mondayOfThisWeek = () => {
  const d = dayjs().startOf("day");
  return d.subtract((d.day() + 6) % 7, "day");
};

const datesForMode = (mode) => {
  const today = todayYMD();
  if (mode === "today") return [today, today];
  if (mode === "yesterday") {
    const d = previousWorkingDay(today, 1);
    return [d, d];
  }
  if (mode === "dayBefore") {
    const d = previousWorkingDay(today, 2);
    return [d, d];
  }
  if (mode === "week") return [toYMD(mondayOfThisWeek()), today];
  if (mode === "lastWeek") {
    const monday = mondayOfThisWeek();
    return [toYMD(monday.subtract(7, "day")), toYMD(monday.subtract(1, "day"))];
  }
  if (mode === "month") return [toYMD(dayjs().startOf("month")), today];
  return null; // custom
};

/* The period just before the selected one, for the comparison chips */
const previousPeriod = (mode, from, to) => {
  if (["today", "yesterday", "dayBefore"].includes(mode)) {
    const d = previousWorkingDay(from, 1);
    return [d, d];
  }
  if (mode === "week" || mode === "lastWeek") {
    return [toYMD(dayjs(from).subtract(7, "day")), toYMD(dayjs(to).subtract(7, "day"))];
  }
  if (mode === "month") {
    return [toYMD(dayjs(from).subtract(1, "month")), toYMD(dayjs(to).subtract(1, "month"))];
  }
  const length = dayjs(to).diff(dayjs(from), "day") + 1;
  const prevTo = dayjs(from).subtract(1, "day");
  return [toYMD(prevTo.subtract(length - 1, "day")), toYMD(prevTo)];
};

const rangeText = (from, to) => {
  const f = dayjs(from);
  const t = dayjs(to);
  if (f.isSame(t, "day")) return f.format("DD MMM YYYY");
  if (f.isSame(t, "year")) return `${f.format("DD MMM")} – ${t.format("DD MMM YYYY")}`;
  return `${f.format("DD MMM YYYY")} – ${t.format("DD MMM YYYY")}`;
};

const shortRangeText = (from, to) => {
  const f = dayjs(from);
  const t = dayjs(to);
  return f.isSame(t, "day") ? f.format("D MMM") : `${f.format("D MMM")}–${t.format("D MMM")}`;
};

const loadInitialRange = () => {
  const mode = localStorage.getItem("review_range_mode") || "month";
  const computed = datesForMode(mode);
  if (computed) return { mode, range: computed };
  const from = localStorage.getItem("review_from");
  const to = localStorage.getItem("review_to");
  if (mode === "custom" && from && to) return { mode, range: [from, to] };
  return { mode: "month", range: datesForMode("month") };
};

const SORTS = {
  name: (a, b) => a.name.localeCompare(b.name),
  sourced: (a, b) => b.cur.sourced - a.cur.sourced,
  sent: (a, b) => b.cur.sent - a.cur.sent,
  attendance: (a, b) => b.cur.attendance_percentage - a.cur.attendance_percentage,
};

/* One table cell: grey 0, bold plain number, or coloured pill */
const ValueCell = ({ count, display, pill, plain }) => {
  if (count === 0) return <Styled.NumSpan zero>{display}</Styled.NumSpan>;
  if (plain || !pill) return <Styled.NumSpan>{display}</Styled.NumSpan>;
  return (
    <Styled.ColorPill bg={pill[0]} fg={pill[1]}>
      {display}
    </Styled.ColorPill>
  );
};

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const ICON_PATHS = {
  up: "M18 15l-6-6-6 6",
  down: "M6 9l6 6 6-6",
  close: "M18 6L6 18M6 6l12 12",
};

const Icon = ({ name }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={ICON_PATHS[name]} />
  </svg>
);

const DeltaChip = ({ cur, prev, prevLabel }) => {
  if (prev == null) return <Styled.DeltaChip tone="flat">No {prevLabel} data</Styled.DeltaChip>;
  const diff = n(cur) - n(prev);
  const tone = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
  return (
    <Styled.DeltaChip tone={tone}>
      {diff > 0 ? "+" : ""}
      {diff} vs {prevLabel}
    </Styled.DeltaChip>
  );
};

/* ---------- Details drawer ---------- */

const RecruiterPanel = ({ row, palette, calendarMonths, rangeLabel, prevLabel, position, onPrev, onNext, onClose }) => {
  const { cur, prev } = row;
  const absent = new Set(cur.absent_dates || []);
  const sent = n(cur.sent);

  const stages = [
    { key: "sourced", label: "Sourced", value: n(cur.sourced), rate: `${perDay(cur.sourced, cur.present_days)} per day`, color: "#4F46E5" },
    { key: "assessed", label: "Assessed", value: n(cur.assessed), rate: `${pct(cur.assessed, cur.sourced)} of sourced`, color: "#7C3AED" },
    { key: "sent", label: "Sent", value: sent, rate: `${perDay(cur.sent, cur.present_days)} per day`, color: "#059669" },
  ];

  return (
    <Styled.Panel>
      <Styled.PanelTop>
        <Styled.NavGroup>
          <Styled.IconBtn type="button" onClick={onPrev} disabled={!onPrev} aria-label="Previous recruiter">
            <Icon name="up" />
          </Styled.IconBtn>
          <Styled.IconBtn type="button" onClick={onNext} disabled={!onNext} aria-label="Next recruiter">
            <Icon name="down" />
          </Styled.IconBtn>
          <span>
            {position.index + 1} of {position.total} recruiters
          </span>
        </Styled.NavGroup>
        <Styled.IconBtn type="button" onClick={onClose} aria-label="Close">
          <Icon name="close" />
        </Styled.IconBtn>
      </Styled.PanelTop>

      <Styled.PanelBody>
      <Styled.Identity>
        <Styled.Avatar bg={palette.bg} fg={palette.fg}>
          {initials(row.name)}
        </Styled.Avatar>
        <div>
          <Styled.Name>{row.name}</Styled.Name>
          <Styled.Sub>{rangeLabel} review</Styled.Sub>
        </div>
      </Styled.Identity>

      <Styled.MetaGrid>
        <Styled.MetaItem>
          <div className="label">Attendance</div>
          <div className="value">
            <Styled.AttendancePill tone={attendanceTone(cur.attendance_percentage)}>
              {Math.round(n(cur.attendance_percentage))}%
            </Styled.AttendancePill>
          </div>
        </Styled.MetaItem>
        <Styled.MetaItem>
          <div className="label">Days present</div>
          <div className="value">
            {cur.present_days} of {cur.working_days}
          </div>
        </Styled.MetaItem>
        <Styled.MetaItem>
          <div className="label">Saturdays worked</div>
          <div className="value">{cur.saturdays_worked}</div>
        </Styled.MetaItem>
      </Styled.MetaGrid>

      <Styled.Section>
        <Styled.SectionHead>
          <h4>Attendance</h4>
          <span>
            {cur.absent_days === 0 ? "No absences" : `${cur.absent_days} days absent`}
          </span>
        </Styled.SectionHead>
        {calendarMonths.map((m) => (
          <Styled.CalMonth key={m.key}>
            {calendarMonths.length > 1 && <Styled.CalMonthLabel>{m.label}</Styled.CalMonthLabel>}
            <Styled.CalGrid>
              {["Mon", "Tue", "Wed", "Thu", "Fri"].map((d) => (
                <Styled.CalHead key={d}>{d}</Styled.CalHead>
              ))}
              {m.days.map((d) => {
                const dj = dayjs(d);
                const isAbsent = absent.has(d);
                return (
                  <Styled.CalDay
                    key={d}
                    col={dj.day()}
                    absent={isAbsent}
                    title={`${dayLabel(d)}: ${isAbsent ? "Absent" : "Present"}`}
                  >
                    {dj.date()}
                  </Styled.CalDay>
                );
              })}
            </Styled.CalGrid>
          </Styled.CalMonth>
        ))}
        <Styled.Legend>
          <span className="present">Present</span>
          <span className="absent">Absent</span>
        </Styled.Legend>
      </Styled.Section>

      <Styled.Section>
        <Styled.SectionHead>
          <h4>Pipeline</h4>
          <span>Compared with {prevLabel}</span>
        </Styled.SectionHead>
        <Styled.StageGrid>
          {stages.map((s) => (
            <Styled.StageTile key={s.key}>
              <div className="label">{s.label}</div>
              <Styled.StageValue color={s.color}>{s.value}</Styled.StageValue>
              <div className="rate">{s.rate}</div>
              <DeltaChip cur={s.value} prev={prev ? prev[s.key] : null} prevLabel={prevLabel} />
            </Styled.StageTile>
          ))}
        </Styled.StageGrid>
      </Styled.Section>

      <Styled.Section>
        <Styled.SectionHead>
          <h4>Sent breakdown</h4>
          <span>{sent} profiles sent</span>
        </Styled.SectionHead>
        <Styled.StackBar>
          {sent > 0 &&
            OUTCOMES.map((o) =>
              n(cur[o.key]) > 0 ? (
                <Styled.StackSeg
                  key={o.key}
                  color={o.color}
                  pct={(n(cur[o.key]) / sent) * 100}
                  title={`${o.label}: ${n(cur[o.key])}`}
                />
              ) : null
            )}
        </Styled.StackBar>
        {OUTCOMES.map((o) => {
          const v = n(cur[o.key]);
          return (
            <Styled.OutcomeRow key={o.key} muted={v === 0}>
              <span className="name">
                <Styled.Dot color={o.color} muted={v === 0} />
                {o.label}
              </span>
              <span className="count">{v}</span>
              <span className="pct">{pct(v, sent)}</span>
            </Styled.OutcomeRow>
          );
        })}
      </Styled.Section>
      </Styled.PanelBody>
    </Styled.Panel>
  );
};

/* ---------- Page ---------- */

const ReviewDashboard = () => {
  const allowed = hasAccess();

  const [initial] = useState(loadInitialRange);
  const [rangeMode, setRangeMode] = useState(initial.mode); // today | yesterday | dayBefore | week | lastWeek | month | custom
  const [range, setRange] = useState(initial.range); // ["YYYY-MM-DD", "YYYY-MM-DD"]
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("number"); // number | percent
  const [filterRecruiter, setFilterRecruiter] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [openId, setOpenId] = useState(null);

  const [rangeFrom, rangeTo] = range;
  const [prevFrom, prevTo] = useMemo(
    () => previousPeriod(rangeMode, rangeFrom, rangeTo),
    [rangeMode, rangeFrom, rangeTo]
  );

  /* Remember the choice, like Performance Report does */
  useEffect(() => {
    localStorage.setItem("review_range_mode", rangeMode);
    localStorage.setItem("review_from", rangeFrom);
    localStorage.setItem("review_to", rangeTo);
  }, [rangeMode, rangeFrom, rangeTo]);

  const applyRange = (key) => {
    setRangeMode(key);
    const computed = datesForMode(key);
    if (computed) setRange(computed);
  };

  const onCustomRange = (values) => {
    if (!values || !values[0] || !values[1]) return;
    let from = values[0].startOf("day");
    let to = values[1].startOf("day");
    if (to.diff(from, "day") + 1 > MAX_RANGE_DAYS) {
      to = from.add(MAX_RANGE_DAYS - 1, "day");
      if (to.isAfter(dayjs(), "day")) to = dayjs().startOf("day");
      toast.info("You can pick up to one year at a time.");
    }
    setRange([toYMD(from), toYMD(to)]);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Selected period and the period before it (for the comparison chips)
      const [res, prevRes] = await Promise.all([
        functionService.getReviewDashboard(rangeFrom, rangeTo),
        functionService.getReviewDashboard(prevFrom, prevTo),
      ]);
      if (res.status) {
        setData(res.response?.data?.data || null);
      } else if (!res.isAuthError) {
        setData(null);
        toast.error(res.error?.response?.data?.message || "Couldn't load the review dashboard. Try again.");
      }
      setPrevData(prevRes.status ? prevRes.response?.data?.data || null : null);
    } catch (err) {
      console.error("ReviewDashboard load error:", err);
      setData(null);
      setPrevData(null);
      toast.error("Couldn't load the review dashboard. Try again.");
    } finally {
      setLoading(false);
    }
  }, [rangeFrom, rangeTo, prevFrom, prevTo]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const allRows = useMemo(() => {
    if (!data) return [];
    const prevById = new Map((prevData?.recruiters || []).map((r) => [r.recruiter_id, r]));
    return data.recruiters
      .map((r) => {
        const prevRecruiter = prevById.get(r.recruiter_id);
        return {
          ...r,
          cur: aggregateMonths(r.months),
          prev: prevRecruiter ? aggregateMonths(prevRecruiter.months) : null,
        };
      })
      .filter((r) => r.cur && hasActivity(r.cur));
  }, [data, prevData]);

  const colorMap = useMemo(() => {
    const map = {};
    [...allRows]
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((r, i) => {
        map[r.recruiter_id] = RECRUITER_PALETTE[i % RECRUITER_PALETTE.length];
      });
    return map;
  }, [allRows]);

  const rows = useMemo(() => {
    const list = filterRecruiter ? allRows.filter((r) => r.recruiter_id === filterRecruiter) : allRows;
    return [...list].sort(SORTS[sortBy]);
  }, [allRows, filterRecruiter, sortBy]);

  /* Grand total row has the same shape as a recruiter's record */
  const total = useMemo(() => {
    const t = {};
    SUM_KEYS.forEach((k) => {
      t[k] = rows.reduce((acc, r) => acc + n(r.cur[k]), 0);
    });
    t.attendance_percentage = t.working_days ? (t.present_days * 100) / t.working_days : 0;
    return t;
  }, [rows]);

  const summaryCards = [
    { label: "Total Sourced", val: total.sourced, color: "#4F46E5", light: "#EEF2FF" },
    { label: "Total Assessed", val: total.assessed, color: "#8B5CF6", light: "#EDE9FE" },
    { label: "Total Sent", val: total.sent, color: "#10B981", light: "#D1FAE5" },
    { label: "Total Backout", val: total.backout, color: "#EF4444", light: "#FEE2E2" },
  ];

  /* Working days per month inside the selected range, for the "Showing" line and the drawer calendar */
  const calendarMonths = (data?.months || []).map((k) => ({
    key: k,
    label: dayjs(`${k}-01`).format("MMMM YYYY"),
    days: (data?.calendar && data.calendar[k]) || [],
  }));
  const workingDayCount = calendarMonths.reduce((acc, m) => acc + m.days.length, 0);
  const includesCurrent = rangeTo >= todayYMD();
  const rangeLabel = rangeText(rangeFrom, rangeTo);
  const prevLabel = shortRangeText(prevFrom, prevTo);

  const openRow = allRows.find((r) => r.recruiter_id === openId);
  const openIndex = rows.findIndex((r) => r.recruiter_id === openId);

  const pipelineDisplay = (col, c) => (mode === "number" ? n(c[col.key]) : col.rate(c));
  const outcomeDisplay = (o, c) => (mode === "number" ? n(c[o.key]) : pct(c[o.key], c.sent));
  const attendanceDisplay = (c) =>
    mode === "number" ? `${n(c.present_days)}/${n(c.working_days)}` : `${Math.round(n(c.attendance_percentage))}%`;

  if (!allowed) {
    return (
      <Styled.MainDiv>
        <Sidemenu />
        <Styled.ContentWrapDiv>
          <Styled.EmptyWrap>
            <div className="icon">🔒</div>
            <div>Review Dashboard is available for access levels 0, 1 and 2.</div>
          </Styled.EmptyWrap>
        </Styled.ContentWrapDiv>
      </Styled.MainDiv>
    );
  }

  return (
    <Styled.MainDiv>
      <Styled.MonthPickerStyles />
      <Sidemenu />
      <Styled.ContentWrapDiv>
        <Styled.TopWrap>
        <Styled.TopContainer>
          <div className="left">
            <Styled.PageTitle>Review Dashboard</Styled.PageTitle>
            <div className="date-label">
              Showing: {rangeLabel}, {workingDayCount} working day{workingDayCount === 1 ? "" : "s"}
              {includesCurrent ? " so far" : ""} (Mon–Fri)
            </div>
          </div>

          <div className="right">
            <Styled.RangeTabsWrapper>
              {[
                { key: "today", label: "Today" },
                { key: "yesterday", label: `Last ${dayjs(previousWorkingDay(todayYMD(), 1)).format("dddd")}` },
                { key: "dayBefore", label: `Last ${dayjs(previousWorkingDay(todayYMD(), 2)).format("dddd")}` },
                { key: "week", label: "This Week" },
                { key: "lastWeek", label: "Last Week" },
                { key: "month", label: "This Month" },
                { key: "custom", label: "Custom" },
              ].map((t) => (
                <Styled.RangeTab key={t.key} active={rangeMode === t.key} onClick={() => applyRange(t.key)}>
                  {t.label}
                </Styled.RangeTab>
              ))}
            </Styled.RangeTabsWrapper>
          </div>
        </Styled.TopContainer>
        </Styled.TopWrap>

        <Styled.FilterRow>
          <Styled.StyledSelectWrap>
            <Select
              showSearch
              allowClear
              placeholder="All recruiters"
              value={filterRecruiter}
              onChange={(v) => setFilterRecruiter(v ?? null)}
              filterOption={(input, option) =>
                String(option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: 190 }}
            >
              {[...allRows]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((r) => {
                  const p = colorMap[r.recruiter_id] || RECRUITER_PALETTE[0];
                  return (
                    <Select.Option key={r.recruiter_id} value={r.recruiter_id} label={r.name}>
                      <Styled.FilterRecruiterChip bg={p.bg} fg={p.fg}>
                        {r.name}
                      </Styled.FilterRecruiterChip>
                    </Select.Option>
                  );
                })}
            </Select>
          </Styled.StyledSelectWrap>

          <Styled.StyledSelectWrap>
            <Select
              value={sortBy}
              onChange={setSortBy}
              style={{ width: 190 }}
              options={[
                { value: "name", label: "Sort: Name" },
                { value: "sourced", label: "Sort: Most sourced" },
                { value: "sent", label: "Sort: Most sent" },
                { value: "attendance", label: "Sort: Best attendance" },
              ]}
            />
          </Styled.StyledSelectWrap>

          {/* Numbers ⇄ Percentages toggle */}
          <Styled.ToggleBox
            on={mode === "percent"}
            role="switch"
            aria-checked={mode === "percent"}
            tabIndex={0}
            onClick={() => setMode((m) => (m === "percent" ? "number" : "percent"))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setMode((m) => (m === "percent" ? "number" : "percent"));
              }
            }}
          >
            <Styled.ToggleLabel active={mode === "number"}>Numbers</Styled.ToggleLabel>
            <Switch
              checked={mode === "percent"}
              onChange={(checked) => setMode(checked ? "percent" : "number")}
              onClick={(_, e) => e && e.stopPropagation()}
              style={{ background: "#4F46E5" }}
            />
            <Styled.ToggleLabel active={mode === "percent"}>Percentages</Styled.ToggleLabel>
          </Styled.ToggleBox>

          {rangeMode === "custom" && (
            <Styled.StyledDatePickerWrap>
              <DatePicker.RangePicker
                className="review-range"
                separator={
                  <Styled.RangeArrow aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  </Styled.RangeArrow>
                }
                value={[dayjs(rangeFrom), dayjs(rangeTo)]}
                format="DD MMM YYYY"
                allowClear={false}
                inputReadOnly
                disabledDate={(d) => d && (d.isAfter(dayjs(), "day") || d.isBefore(MIN_DATE, "day"))}
                onChange={onCustomRange}
                style={{ width: 290 }}
              />
            </Styled.StyledDatePickerWrap>
          )}
        </Styled.FilterRow>

        <Styled.SummaryGrid4>
          {summaryCards.map((card, idx) => (
            <Styled.SummaryCard key={card.label} color={card.color} light={card.light} idx={idx}>
              <Styled.SummaryValue color={card.color}>{card.val}</Styled.SummaryValue>
              <Styled.SummaryLabel>{card.label}</Styled.SummaryLabel>
            </Styled.SummaryCard>
          ))}
        </Styled.SummaryGrid4>

        <Styled.TableScrollArea>
          {loading ? (
            <Styled.SpinWrap>
              <Spin size="large" />
            </Styled.SpinWrap>
          ) : rows.length === 0 ? (
            <Styled.EmptyWrap>
              <div className="icon">📋</div>
              <div>No recruiter activity in {rangeLabel}</div>
            </Styled.EmptyWrap>
          ) : (
            <Styled.MainTable>
              <thead>
                <tr>
                  <Styled.THMeta left rowSpan={2} style={{ width: "16%" }}>Recruiter</Styled.THMeta>
                  <Styled.THMeta rowSpan={2} style={{ width: "8%" }}>Attendance</Styled.THMeta>
                  <Styled.THMeta rowSpan={2} style={{ width: "6%" }}>Saturdays</Styled.THMeta>
                  <Styled.THGroup colSpan={3} groupColor="#4F46E5">Pipeline</Styled.THGroup>
                  <Styled.THGroup colSpan={6} groupColor="#F59E0B">Sent Breakdown</Styled.THGroup>
                </tr>
                <tr>
                  {PIPELINE.map((col) => (
                    <Styled.THSub key={col.key} bg={col.head[0]} color={col.head[1]}>
                      {col.label}
                    </Styled.THSub>
                  ))}
                  {OUTCOMES.map((o) => (
                    <Styled.THSub
                      key={o.key}
                      bg={o.pill[0]}
                      color={o.pill[1]}
                      style={{ whiteSpace: "normal", lineHeight: 1.25, minWidth: 84 }}
                    >
                      {o.label}
                    </Styled.THSub>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.map((r) => {
                  const c = r.cur;
                  const palette = colorMap[r.recruiter_id] || RECRUITER_PALETTE[0];
                  return (
                    <Styled.RecruiterRow key={r.recruiter_id} onClick={() => setOpenId(r.recruiter_id)}>
                      <td className="left">
                        <Styled.RecruiterNameCell>
                          <Styled.RecruiterChip bg={palette.bg} fg={palette.fg}>
                            {r.name}
                          </Styled.RecruiterChip>
                          <Styled.RowHint>Details</Styled.RowHint>
                        </Styled.RecruiterNameCell>
                      </td>
                      <td>
                        <Styled.AttendancePill tone={attendanceTone(c.attendance_percentage)}>
                          {attendanceDisplay(c)}
                        </Styled.AttendancePill>
                      </td>
                      <td>
                        <ValueCell count={n(c.saturdays_worked)} display={n(c.saturdays_worked)} plain />
                      </td>
                      {PIPELINE.map((col) => (
                        <td key={col.key}>
                          <ValueCell
                            count={n(c[col.key])}
                            display={pipelineDisplay(col, c)}
                            pill={col.pill}
                            plain={col.plain}
                          />
                        </td>
                      ))}
                      {OUTCOMES.map((o) => (
                        <td key={o.key}>
                          <ValueCell count={n(c[o.key])} display={outcomeDisplay(o, c)} pill={o.pill} />
                        </td>
                      ))}
                    </Styled.RecruiterRow>
                  );
                })}

                <Styled.GrandTotalRow>
                  <td className="left label">Grand Total</td>
                  <td>{attendanceDisplay(total)}</td>
                  <td>{total.saturdays_worked}</td>
                  {PIPELINE.map((col) => (
                    <td key={col.key}>{pipelineDisplay(col, total)}</td>
                  ))}
                  {OUTCOMES.map((o) => (
                    <td key={o.key}>{outcomeDisplay(o, total)}</td>
                  ))}
                </Styled.GrandTotalRow>
              </tbody>
            </Styled.MainTable>
          )}
        </Styled.TableScrollArea>

        <Drawer
          open={!!openRow}
          onClose={() => setOpenId(null)}
          width="min(520px, 100vw)"
          destroyOnClose
          closable={false}
          title={null}
          className="review-drawer"
          styles={{ body: { padding: 0 } }}
        >
          {openRow && (
            <RecruiterPanel
              row={openRow}
              palette={colorMap[openRow.recruiter_id] || RECRUITER_PALETTE[0]}
              calendarMonths={calendarMonths}
              rangeLabel={rangeLabel}
              prevLabel={prevLabel}
              position={{ index: Math.max(openIndex, 0), total: rows.length }}
              onPrev={openIndex > 0 ? () => setOpenId(rows[openIndex - 1].recruiter_id) : null}
              onNext={
                openIndex >= 0 && openIndex < rows.length - 1
                  ? () => setOpenId(rows[openIndex + 1].recruiter_id)
                  : null
              }
              onClose={() => setOpenId(null)}
            />
          )}
        </Drawer>
      </Styled.ContentWrapDiv>
    </Styled.MainDiv>
  );
};

export default ReviewDashboard;